import React, { useState, useEffect } from 'react';
import { Gallery, DisplaySettings } from '../../types/Gallery';
import { loadLiveCategories } from '../../utils/CategoryAPI';
import ImageCard from './ImageCard';
import GalleryModal from './GalleryModal';
import './MagazineGallery.css';

interface MagazineGalleryProps {
  galleries: Gallery[];
  settings: DisplaySettings;
}

const MagazineGallery: React.FC<MagazineGalleryProps> = ({ galleries, settings }) => {
  // Ensure galleryCount doesn't exceed actual available galleries
  const maxGalleries = settings.galleryCount || galleries.length;
  const actualGalleryCount = Math.min(maxGalleries, galleries.length);
  const displayGalleries = galleries.slice(0, actualGalleryCount);
  const [selectedGallery, setSelectedGallery] = useState<Gallery | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);

  const handleGalleryClick = (gallery: Gallery) => {
    setSelectedGallery(gallery);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedGallery(null);
  };

  // Load categories
  useEffect(() => {
    const loadCategoriesData = async () => {
      try {
        const categoriesData = await loadLiveCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategoriesData();

    const handleCategoryUpdate = () => {
      setTimeout(() => {
        loadCategoriesData();
      }, 100);
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'admin_categories') {
        setTimeout(() => {
          loadCategoriesData();
        }, 100);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('category_updated', handleCategoryUpdate);
    window.addEventListener('categories_updated', handleCategoryUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('category_updated', handleCategoryUpdate);
      window.removeEventListener('categories_updated', handleCategoryUpdate);
    };
  }, []);

  // Get category color
  const getCategoryColor = (categoryName: string): string => {
    const normalizedInput = (categoryName || '').toLowerCase().trim();
    const category = categories.find(cat =>
      (cat.name || '').toLowerCase().trim() === normalizedInput ||
      (cat.slug || '').toLowerCase().trim() === normalizedInput
    );
    return category?.color || '#8B4513';
  };

  // Apply theme settings
  useEffect(() => {
    const loadThemeAndApply = () => {
      const currentTheme = localStorage.getItem('admin-theme');
      if (currentTheme) {
        try {
          const themeData = JSON.parse(currentTheme);
          const root = document.documentElement;

          if (themeData.palette) {
            root.style.setProperty('--color-primary', themeData.palette.primary || '#8B4513');
            root.style.setProperty('--color-secondary', themeData.palette.secondary || '#A0522D');
            root.style.setProperty('--color-accent', themeData.palette.accent || '#D4AF37');
            root.style.setProperty('--color-background', themeData.palette.background || '#FFFFFF');
            root.style.setProperty('--color-text', themeData.palette.text || '#8B4513');
            root.style.setProperty('--color-surface', themeData.palette.surface || '#F8F9FA');
            root.style.setProperty('--color-border', themeData.palette.border || '#E1E1E1');
          }
        } catch (error) {
          console.error('Error loading theme settings:', error);
        }
      }
    };

    loadThemeAndApply();

    const handleThemeChange = () => {
      loadThemeAndApply();
    };

    window.addEventListener('storage', handleThemeChange);
    window.addEventListener('theme_updated', handleThemeChange);
    
    return () => {
      window.removeEventListener('storage', handleThemeChange);
      window.removeEventListener('theme_updated', handleThemeChange);
    };
  }, []);

  if (displayGalleries.length === 0) {
    return (
      <div className="magazine-gallery-container">
        <div className="no-galleries">
          <h2>Portfolio Coming Soon</h2>
          <p>My photography collections are being prepared with love and artistry...</p>
        </div>
      </div>
    );
  }

  // Create editorial layout - first gallery is large, others are smaller
  const featuredGallery = displayGalleries[0];
  const otherGalleries = displayGalleries.slice(1);

  return (
    <>
      <div className="magazine-gallery-container" data-count={displayGalleries.length}>
        <div className="magazine-layout">
          {/* Featured Gallery - Large */}
          {featuredGallery && (
            <div className="magazine-featured">
              <ImageCard 
                gallery={featuredGallery}
                feature={true}
                showcase={true}
                className="magazine-featured-card"
                onGalleryClick={handleGalleryClick}
                categoryColor={getCategoryColor(featuredGallery.category)}
              />
            </div>
          )}

          {/* Other Galleries - Grid */}
          {otherGalleries.length > 0 && (
            <div className="magazine-grid">
              {otherGalleries.map((gallery) => (
                <ImageCard 
                  key={gallery.id} 
                  gallery={gallery}
                  showcase={true}
                  className="magazine-grid-card"
                  onGalleryClick={handleGalleryClick}
                  categoryColor={getCategoryColor(gallery.category)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <GalleryModal 
        gallery={selectedGallery}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </>
  );
};

export default React.memo(MagazineGallery);
